// Read-only membership VERIFY panel (SLICE-MEMBERSHIP-PASS). Shown after a
// scan resolves to a membership card: the staff/owner scanner POSTs the
// scanned token to the membership/checkin endpoint, which logs the check-in
// and returns this data. There is NO stamp/redeem action — a membership scan
// is a verification, not a transaction.
//
// Reuses the shared stamp-overlay chrome (StampOverlayShell + the .stamp-
// overlay* CSS) so the surface reads identically to the loyalty stamp overlay.

import {
  StampOverlayShell,
  StampOverlaySkeleton,
} from '../stamp/StampOverlayChrome.js'
import {
  formatExpiry,
  membershipStatusLabel,
  relativeTimeAgo,
  type MembershipCheckinResponse,
} from './membershipTypes.js'
import './membership.css'

export function MembershipVerifyPanel({
  data,
  loading,
  error,
  onDone,
}: {
  /** The checkin response. Null while the check-in POST is in flight. */
  data: MembershipCheckinResponse | null
  loading: boolean
  /** A human error string if the check-in failed (verify could not complete). */
  error: string | null
  onDone: () => void
}) {
  if (error) {
    return (
      <StampOverlayShell ariaLabelledBy="membership-verify-name">
        <div id="membership-verify-name" className="stamp-overlay-name">
          Couldn't verify
        </div>
        <p className="stamp-overlay-hint" style={{ margin: '0 auto 18px' }}>
          {error}
        </p>
        <div className="stamp-overlay-actions">
          <button type="button" className="owner-btn" onClick={onDone}>
            Close
          </button>
        </div>
      </StampOverlayShell>
    )
  }

  if (loading || !data) {
    return (
      <StampOverlayShell ariaLabelledBy="membership-verify-name">
        <StampOverlaySkeleton onCancel={onDone} />
      </StampOverlayShell>
    )
  }

  const name = data.member_name?.trim() || 'Member'
  const lastVisit = relativeTimeAgo(data.last_check_in_at)
  const expiry = formatExpiry(data.expires_at)

  return (
    <StampOverlayShell ariaLabelledBy="membership-verify-name">
      <div className="membership-verify-eyebrow">
        {data.program_name?.trim() || 'Membership'}
      </div>
      <div id="membership-verify-name" className="stamp-overlay-name">
        Welcome back, {name}
      </div>

      <div className="membership-verify-status">
        <StatusPill status={data.status} />
      </div>

      <dl className="membership-verify-fields">
        {data.tier_name && (
          <div className="membership-verify-field">
            <dt>Tier</dt>
            <dd>{data.tier_name}</dd>
          </div>
        )}
        <div className="membership-verify-field">
          <dt>{data.member_number_label}</dt>
          <dd className="membership-verify-number">{data.member_number}</dd>
        </div>
        {expiry && (
          <div className="membership-verify-field">
            <dt>{data.status === 'expired' ? 'Expired' : 'Valid until'}</dt>
            <dd>{expiry}</dd>
          </div>
        )}
        <div className="membership-verify-field">
          <dt>Last visit</dt>
          <dd>{lastVisit ?? 'First visit'}</dd>
        </div>
      </dl>

      <div className="stamp-overlay-actions">
        <button
          type="button"
          className="owner-btn owner-btn-primary owner-btn-block"
          onClick={onDone}
        >
          Done
        </button>
      </div>
    </StampOverlayShell>
  )
}

export function StatusPill({ status }: { status: MembershipCheckinResponse['status'] }) {
  return (
    <span className={`membership-pill membership-pill-${status}`}>
      <span className="membership-pill-dot" aria-hidden="true" />
      {membershipStatusLabel(status)}
    </span>
  )
}
